<template>
  <section class="detail-view">
    <div class="detail-header">
      <button class="text-button" type="button" @click="$emit('back')">
        {{ $t('support_back') }}
      </button>
      <div class="detail-title">
        <h1>{{ aqId || title }}</h1>
        <p>{{ detailMeta }}</p>
      </div>
      <div class="detail-actions">
        <span class="status-badge" :class="statusClass">{{ chatStatusText }}</span>
        <button class="ghost-button compact" type="button" @click="$emit('back')">
          {{ $t('support_mark_solved') }}
        </button>
      </div>
    </div>

    <div ref="messageList" class="message-list">
      <div class="message-day" v-if="ticket.length">
        <span>{{ formatDay(ticket[0].time) }}</span>
      </div>

      <div
        v-for="(item, i) in ticket"
        :key="i"
        class="message"
        :class="Number(item.sender) === 1 ? 'agent' : 'player'"
      >
        <div class="bubble">
          <div class="message-name">
            <span>{{ Number(item.sender) === 1 ? $t('support_official_support') : userName }}</span>
            <time>{{ formatClock(item.time) }}</time>
          </div>
          <div class="bubble-content" v-if="item.message">{{ item.message }}</div>
          <div class="message-attachments" v-if="attachmentUrls(item).length">
            <template v-for="url in attachmentUrls(item)">
              <video v-if="hasVideo(url)" :key="url" :src="url" controls playsinline></video>
              <img v-else :key="url" :src="url" :alt="$t('support_attachment_preview')" @click="previewImage(url)">
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="rating-panel" v-if="isClosedTicket">
      <span>{{ $t('support_rate_title') }}</span>
      <el-rate v-model="rating" @change="onRateChange" />
    </div>

    <form class="reply-box" v-else @submit.prevent="sendMessage">
      <div class="reply-toolbar">
        <el-upload
          class="reply-upload"
          :show-file-list="false"
          action="/"
          :http-request="uploadRequest"
          :disabled="imageUploading"
        >
          <button class="tool-button" type="button" :disabled="imageUploading">
            <span class="image-icon" aria-hidden="true"></span>
            <span>{{ $t('support_image') }}</span>
          </button>
        </el-upload>
        <span class="reply-hint">{{ $t('support_reply_hint') }}</span>
      </div>
      <div class="reply-row">
        <el-input
          v-model="inputText"
          type="textarea"
          maxlength="500"
          :autosize="false"
          :placeholder="$t('support_input_placeholder')"
          class="reply-input"
        />
        <button class="primary-button send-button" type="submit" :disabled="textUploading">
          {{ textUploading ? $t('support_sending') : $t('support_send') }}
        </button>
      </div>
    </form>

    <el-dialog :visible.sync="imgPreviewVisible" append-to-body :width="isMobile ? '100%' : '60%'">
      <div class="img-preview-container">
        <img v-if="imgPreviewSrc" :src="imgPreviewSrc" class="img-preview" :alt="$t('support_attachment_preview')" />
      </div>
    </el-dialog>
  </section>
</template>

<script>
import { uploadMedia } from '@/utils/uploadService.js'
import { replyTicket, rateTicket } from '@/utils/api/supportApi.js'

export default {
  name: 'SupportChat',
  props: {
    ticket: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    aqId: {
      type: [Number, String],
      default: ''
    },
    currentTicket: {
      type: Object,
      default: () => ({})
    },
    supportSession: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      rating: 0,
      inputText: '',
      imageUploading: false,
      textUploading: false,
      imgPreviewVisible: false,
      imgPreviewSrc: '',
      isMobile: false
    }
  },
  computed: {
    userName() {
      const loginData = this.$cache.getLoginData() || {}
      const roleInfo = this.$cache.getRoleInfo() || {}
      return roleInfo.roleId || loginData.userId || this.$session.getUserId() || this.$t('support_player')
    },
    lastStatus() {
      if (this.currentTicket && this.currentTicket.status) return this.currentTicket.status
      const arr = Array.isArray(this.ticket) ? this.ticket : []
      const last = arr[arr.length - 1] || {}
      return last.status
    },
    chatStatusText() {
      const key = String(this.lastStatus || '').toUpperCase()
      if (key === 'RESOLVED' || key === 'CANCELLED' || key === '2' || key === '3') return this.$t('support_status_resolved')
      if (key === 'PROCESSING' || key === '4') return this.$t('support_status_pending_review')
      if (key === 'HANDLED' || key === '1') return this.$t('support_status_replied')
      return this.$t('support_status_unreplied')
    },
    statusClass() {
      const key = String(this.lastStatus || '').toUpperCase()
      if (key === 'NEW' || key === '0') return 'status-new'
      if (key === 'PROCESSING' || key === '4') return 'status-processing'
      if (key === 'RESOLVED' || key === 'CANCELLED' || key === '2' || key === '3') return 'status-resolved'
      return 'status-handled'
    },
    isClosedTicket() {
      const key = String(this.lastStatus || '').toUpperCase()
      return key === 'RESOLVED' || key === 'CANCELLED' || key === '2' || key === '3'
    },
    detailMeta() {
      const roleInfo = this.$cache.getRoleInfo() || {}
      const gameData = this.$store.state.gameData || {}
      const first = (Array.isArray(this.ticket) && this.ticket[0]) || {}
      return [this.title || this.$t('support_my_questions'), gameData.gameName, roleInfo.serverName, first.time].filter(Boolean).join(' / ')
    }
  },
  watch: {
    ticket: {
      handler(val) {
        const score = this.extractScore(val)
        if (typeof score === 'number' && !isNaN(score)) this.rating = score
        this.$nextTick(this.scrollToBottom)
      },
      immediate: true
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    previewImage(src) {
      this.imgPreviewSrc = src
      this.imgPreviewVisible = true
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    hasVideo(src) {
      if (!src || typeof src !== 'string') return false
      if (src.startsWith('data:video')) return true
      const base = src.split('?')[0].toLowerCase()
      return /\.(mp4|webm|ogg|mov|m4v)$/i.test(base)
    },
    attachmentUrls(item) {
      const value = (item && (item.attachments || item.imgUrl)) || ''
      return String(value)
        .split(',')
        .map(url => url.trim())
        .filter(Boolean)
    },
    postReply(payload) {
      const data = typeof payload === 'string' ? { message: payload } : (payload || {})
      return replyTicket(this.supportSession, this.currentTicket || { id: this.aqId }, {
        content: data.message || '',
        attachments: data.imgUrl || '',
        attachmentFallback: this.$t('support_attachment_fallback')
      })
    },
    appendMessage(text, img) {
      if (!Array.isArray(this.ticket)) return
      this.ticket.push({
        message: text || '',
        sender: 0,
        status: 0,
        time: this.formatNow(),
        imgUrl: img || ''
      })
      this.$nextTick(this.scrollToBottom)
    },
    scrollToBottom() {
      const body = this.$refs.messageList
      if (body) body.scrollTop = body.scrollHeight
    },
    uploadRequest({ file, onSuccess, onError }) {
      this.imageUploading = true
      uploadMedia(file, { space: 'cs', isReduce: true })
        .then(({ url }) => {
          onSuccess && onSuccess({ url })
          return this.postReply({ message: '', imgUrl: url }).then(() => {
            this.appendMessage('', url)
            this.$emit('refresh')
          })
        })
        .catch(err => {
          onError && onError(err)
          const msg =
            err && err.code === 'FILE_TOO_LARGE'
              ? (err.isVideo ? this.$t('support_form_video_too_big') : this.$t('support_form_image_too_big'))
              : this.$t('support_send_fail')
          this.$message.error(msg)
        })
        .finally(() => {
          this.imageUploading = false
        })
    },
    extractScore(arr) {
      if (!arr) return null
      if (typeof arr.score !== 'undefined') {
        const score = Number(arr.score)
        return isNaN(score) ? null : score
      }
      if (Array.isArray(arr)) {
        for (let i = arr.length - 1; i >= 0; i--) {
          const score = Number(arr[i] && arr[i].score)
          if (!isNaN(score)) return score
        }
      }
      return null
    },
    sendMessage() {
      const msg = this.inputText.trim()
      if (!msg || this.textUploading) return
      this.textUploading = true
      this.postReply({ message: msg }).then(() => {
        this.inputText = ''
        this.appendMessage(msg, '')
        this.$emit('refresh')
      }).catch(() => {
        this.$message.error(this.$t('support_submit_fail'))
      }).finally(() => {
        this.textUploading = false
      })
    },
    onRateChange(val) {
      this.rating = val
      rateTicket(this.supportSession, this.currentTicket || { id: this.aqId }, val).then(() => {
        this.$message.success(this.$t('support_evaluate_success'))
      }).catch(() => {
        this.$message.error(this.$t('support_evaluate_fail'))
      })
    },
    formatDay(t) {
      if (!t) return ''
      const str = String(t)
      return str.split(' ')[0].replace(/\./g, '-')
    },
    formatClock(t) {
      if (!t) return ''
      const str = String(t)
      return str.split(' ')[1] || str
    },
    formatNow() {
      const date = new Date()
      const pad = num => String(num).padStart(2, '0')
      return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
  }
}
</script>

<style scoped>
.detail-view {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 0 0 auto;
  padding: 16px 18px;
  border-bottom: 1px solid var(--support-line-soft);
  background: #fff;
}

.text-button {
  height: 40px;
  border: 0;
  border-radius: 8px;
  padding: 0 10px;
  color: var(--support-primary-strong);
  font-size: .175rem;
  font-weight: 700;
  background: transparent;
  cursor: pointer;
}

.detail-title {
  min-width: 0;
  flex: 1;
}

.detail-title h1 {
  margin: 0;
  overflow: hidden;
  color: var(--support-text);
  font-size: .175rem;
  font-weight: 800;
  line-height: 31px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-title p {
  margin: 3px 0 0;
  overflow: hidden;
  color: var(--support-muted);
  font-size: .175rem;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ghost-button {
  height: 40px;
  border: 1px solid var(--support-line);
  border-radius: 8px;
  padding: 0 18px;
  color: #42526a;
  font-size: .175rem;
  font-weight: 700;
  background: #fff;
  cursor: pointer;
}

.ghost-button.compact {
  min-width: 72px;
  height: 34px;
  padding: 0 12px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  height: 26px;
  border-radius: 13px;
  padding: 0 10px;
  color: var(--support-primary-strong);
  font-size: .175rem;
  line-height: 26px;
  white-space: nowrap;
  background: var(--support-primary-soft);
}

.status-new {
  color: #e74c3c;
  background: #fff1ef;
}

.status-processing {
  color: #a96600;
  background: #fff6df;
}

.status-handled,
.status-resolved {
  color: #18a765;
  background: #eaf9f1;
}

.message-list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 18px;
  background: #f7f9fc;
}

.message-day {
  display: flex;
  justify-content: center;
  margin: 4px 0 12px;
}

.message-day span {
  border-radius: 12px;
  padding: 4px 9px;
  color: #8190a3;
  font-size: .175rem;
  background: rgba(23, 32, 51, .06);
}

.message {
  display: flex;
  margin-bottom: 12px;
}

.message.player {
  justify-content: flex-end;
}

.message.agent {
  justify-content: flex-start;
}

.bubble {
  max-width: 76%;
  border: 1px solid var(--support-line);
  border-radius: 8px;
  padding: 10px 12px;
  color: var(--support-text);
  background: #fff;
}

.message.player .bubble {
  border-color: var(--support-primary-line);
  background: var(--support-primary-soft);
}

.message.agent .bubble {
  border-color: #ccefdc;
  background: #effbf4;
}

.message-name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
  color: var(--support-muted);
  font-size: .175rem;
  line-height: 18px;
}

.bubble-content {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: .175rem;
  line-height: 24px;
}

.message-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.message-attachments img,
.message-attachments video {
  width: 94px;
  height: 74px;
  border: 1px solid rgba(0, 0, 0, .06);
  border-radius: 8px;
  object-fit: cover;
}

.rating-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 0 0 auto;
  padding: 10px 14px;
  border-top: 1px solid var(--support-line-soft);
  color: var(--support-muted);
  font-size: .175rem;
  background: #fff;
}

.reply-box {
  flex: 0 0 auto;
  padding: 12px 16px;
  border-top: 1px solid var(--support-line-soft);
  background: #fff;
}

.reply-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 7px;
}

.tool-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  border: 1px solid var(--support-primary-line);
  border-radius: 8px;
  padding: 0 10px;
  color: var(--support-primary-strong);
  font-size: .175rem;
  background: #fff;
  cursor: pointer;
}

.image-icon {
  position: relative;
  display: inline-flex;
  width: 18px;
  height: 18px;
  border: 1.5px solid currentColor;
  border-radius: 5px;
}

.image-icon::before {
  content: "";
  position: absolute;
  inset: 4px;
  border: 2px solid currentColor;
  border-radius: 50%;
}

.reply-hint {
  color: var(--support-muted);
  font-size: .175rem;
}

.reply-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 82px;
  align-items: stretch;
  gap: 8px;
}

::v-deep .reply-input .el-textarea__inner {
  height: 52px !important;
  min-height: 52px !important;
  border: 1px solid #d4deeb;
  border-radius: 8px;
  padding: 8px 10px;
  color: var(--support-text);
  font-size: .175rem;
  line-height: 24px;
  resize: none;
  background: #fbfdff;
}

::v-deep .reply-input .el-textarea__inner:focus {
  border-color: var(--support-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--support-primary) 14%, transparent);
}

.primary-button {
  border: 0;
  border-radius: 8px;
  color: #fff;
  font-weight: 700;
  font-size: .175rem;
  background: var(--support-primary);
  cursor: pointer;
}

.send-button {
  width: 96px;
  min-width: 96px;
  height: 52px;
  padding: 0;
}

.img-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 60vh;
  background: #000;
}

.img-preview {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
}

@media (max-width: 768px) {
  .detail-view {
    min-height: 100%;
    background: #f8fbff;
  }

  .detail-header {
    align-items: center;
    gap: .18rem;
    min-height: 1.42rem;
    padding: .22rem .32rem;
    border-bottom-color: #e6edf6;
    background: #fff;
  }

  .text-button {
    flex: 0 0 auto;
    height: .72rem;
    padding: 0 .1rem;
    font-size: .3rem;
    line-height: .72rem;
  }

  .detail-title {
    flex: 1;
    min-width: 0;
  }

  .detail-title h1 {
    max-width: 3rem;
    font-size: .36rem;
    line-height: .46rem;
  }

  .detail-title p {
    max-width: 3.2rem;
    font-size: .24rem;
    line-height: .32rem;
    white-space: nowrap;
  }

  .detail-actions {
    flex: 0 0 auto;
    flex-direction: row;
    align-items: center;
    gap: .12rem;
  }

  .ghost-button.compact {
    min-width: 1.4rem;
    height: .72rem;
    border-radius: .14rem;
    padding: 0 .2rem;
    font-size: .3rem;
  }

  .status-badge {
    height: .48rem;
    border-radius: .24rem;
    padding: 0 .18rem;
    font-size: .24rem;
    line-height: .48rem;
  }

  .message-list {
    padding: .28rem .32rem .3rem;
    background: #f8fbff;
  }

  .message-day {
    margin: .08rem 0 .22rem;
  }

  .message-day span {
    border-radius: .24rem;
    padding: .08rem .18rem;
    font-size: .24rem;
  }

  .bubble {
    max-width: 82%;
    border-radius: .14rem;
    padding: .18rem .22rem;
  }

  .message.player .bubble {
    background: var(--support-primary-soft);
  }

  .message.agent .bubble {
    border-color: #bdeacb;
    background: #eefbf4;
  }

  .message-name {
    gap: .14rem;
    margin-bottom: .08rem;
    font-size: .24rem;
    line-height: .32rem;
  }

  .bubble-content {
    font-size: .3rem;
    line-height: .42rem;
  }

  .message-attachments {
    gap: .14rem;
  }

  .message-attachments img,
  .message-attachments video {
    width: 1.6rem;
    height: 1.2rem;
    border-radius: .14rem;
  }

  .reply-box {
    padding: .22rem .32rem .28rem;
    border-top-color: #e6edf6;
    background: #fff;
  }

  .reply-toolbar {
    align-items: center;
    margin-bottom: .16rem;
  }

  .tool-button {
    height: .72rem;
    border-radius: .14rem;
    padding: 0 .22rem;
    font-size: .3rem;
  }

  .reply-hint {
    overflow: hidden;
    color: #73829a;
    font-size: .24rem;
    line-height: .32rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .reply-row {
    grid-template-columns: minmax(0, 1fr) 1.72rem;
    gap: .16rem;
  }

  ::v-deep .reply-input .el-textarea__inner {
    height: .96rem !important;
    min-height: .96rem !important;
    border-color: #d9e4f1;
    border-radius: .14rem;
    padding: .18rem .22rem;
    font-size: .32rem;
    line-height: .42rem;
    background: #fff;
  }

  .send-button {
    width: 1.72rem;
    min-width: 1.72rem;
    height: .96rem;
    border-radius: .16rem;
    font-size: .34rem;
  }
}
</style>
